import React, { useEffect } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useGetItemByIdQuery } from "../../services/items/items";
import { Item, ItemMobile } from "../../components/Item/Item";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import ProductDetailSkeleton from "../../components/ProductDetailSkeleton/ProductDetailSkeleton";
import Seo from "../../components/Seo/Seo";
import { SEO_DEFAULTS } from "../../seo/seoConstants";
import { ItemType } from "../../types/types";
import { useAddToCart } from "../shared/utils";
import { useCartDrawer } from "../../components/CartComponent/CartDrawerProvider";
import { gaEvent, gaValue, toGaItem } from "../../analytics";
import { NAVIGATE_DELAY_MS, PRODUCT_DETAIL_TEXTS } from "../shared/constants";

export const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const addToCart = useAddToCart();

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const { data, isFetching, isError } = useGetItemByIdQuery(id ?? "");

  // `GET /items/{id}` has no active-only mode — the admin edit screen reads the same endpoint and
  // needs inactive rows to curate them — so the storefront filters here instead. Without this the
  // §5.2 filter is a half-truth: the product drops out of the listing but its URL keeps working,
  // and those URLs are shared on WhatsApp (EB-503). Strict `=== true` deliberately mirrors the
  // backend's own check in `lambda/items/handlers/listItems.mjs`, so the detail page can never
  // show a product the listing hid.
  const item = data?.is_active === true ? data : undefined;

  useEffect(() => {
    if (item) {
      gaEvent("view_item", {
        currency: "COP",
        value: gaValue([item]),
        items: [toGaItem(item)],
      });
    }
  }, [item]);

  const { openCart } = useCartDrawer();

  /**
   * Adding shows the cart (EB-556) instead of a toast.
   *
   * The toast said "¡Agregado al carrito!" and disappeared; the shopper then had to find the cart
   * icon to check what was in it. The drawer answers the same question and the next one — what do I
   * have, and what does it cost — and it puts the checkout button one tap away at the moment the
   * shopper is most likely to want it. Both at once would have been noise: the drawer opens over
   * the bottom-right corner the snackbar occupied.
   */
  const addToCartHandler = (payload: ItemType, quantity: number) => {
    addToCart(payload, quantity);
    openCart("product_detail");
  };

  const handlePurchase = (payload: ItemType, quantity: number) => {
    addToCart(payload, quantity);
    setTimeout(() => navigate("/hacer-pedido"), NAVIGATE_DELAY_MS);
  };

  return (
    <PageLayout>
      {item && (
        <Seo
          title={`${item.name} | ${SEO_DEFAULTS.siteName}`}
          description={item.description || undefined}
          canonical={`${SEO_DEFAULTS.baseUrl}/producto/${id}`}
        />
      )}
      {isFetching ? (
        <Box py={1}>
          <ProductDetailSkeleton variant={isSmall ? "mobile" : "desktop"} />
        </Box>
      ) : isError ? (
        <Typography variant="subtitle2" fontWeight="bold" color="error.main">
          {PRODUCT_DETAIL_TEXTS.errorLoading}
        </Typography>
      ) : item ? (
        <Box py={1}>
          {isSmall ? (
            <ItemMobile
              item={item}
              onAddToCart={(quantity) => addToCartHandler(item, quantity)}
              handlePurchase={(quantity) => {
                handlePurchase(item, quantity);
              }}
            />
          ) : (
            <Item
              item={item}
              onAddToCart={(quantity) => addToCartHandler(item, quantity)}
              handlePurchase={(quantity) => {
                handlePurchase(item, quantity);
              }}
            />
          )}
        </Box>
      ) : (
        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
          {PRODUCT_DETAIL_TEXTS.notFound}
        </Typography>
      )}
    </PageLayout>
  );
};

export default ProductDetailPage;
